import React, { useState, useEffect } from 'react';
import { 
  GripVertical,
  Save,
  RotateCcw,
  AlertCircle,
  Zap,
  Briefcase,
  Users,
  Clock,
  FileText,
  Calendar,
  TrendingUp,
  MessageCircle,
  Mail,
  Bell
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useUpgrade } from '../../contexts/UpgradeContext';
import { useTranslation } from '../../i18n/hooks/useTranslation';

interface DashboardWidget {
  id: string;
  type: 'quickAction' | 'rightMenuAction';
  label: string;
  icon: string;
  path?: string;
  action?: string;
  description?: string;
  color?: string;
  order: number;
  enabled: boolean;
}

const iconMap: Record<string, any> = {
  Briefcase,
  Users,
  Clock,
  FileText,
  Calendar,
  TrendingUp,
  MessageCircle,
  Mail,
  Bell
};

export const MainExperienceCards: React.FC = () => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { openUpgradeModal } = useUpgrade();
  const { t } = useTranslation();
  const [widgets, setWidgets] = useState<DashboardWidget[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState<string>('premium');

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const canEdit = !!hasPermission('Dashboard', 'edit');

  const getWidgetLabel = (widgetId: string): string => {
    const labelMap: Record<string, string> = {
      'projects': t('admin.widgetLabelProjects'),
      'staff': t('admin.widgetLabelStaff'),
      'timesheets': t('admin.widgetLabelTimesheets'),
      'contacts': t('admin.widgetLabelContacts'),
      'calendar': t('admin.widgetLabelCalendar'),
      'leave': t('admin.widgetLabelLeave'),
      'admin': t('admin.widgetLabelAdmin'),
      'aiChat': t('admin.widgetLabelAIChat'),
      'createMessage': t('admin.widgetLabelCreateMessage'),
      'notifications': t('admin.widgetLabelNotifications'),
      'createTimesheet': t('admin.widgetLabelCreateTimesheet'),
      'dashboard-aiChat': t('admin.widgetLabelAIChat'),
      'dashboard-createMessage': t('admin.widgetLabelCreateMessage'),
      'dashboard-notifications': t('admin.widgetLabelNotifications'),
      'dashboard-createTimesheet': t('admin.widgetLabelCreateTimesheet'),
      'dashboard-createLeave': t('admin.widgetLabelCreateLeave')
    };
    return labelMap[widgetId] || widgetId;
  };

  const getWidgetDescription = (widgetId: string): string => {
    const descMap: Record<string, string> = {
      'projects': t('dashboard.widgetManageProjects'),
      'staff': t('dashboard.widgetViewStaff'),
      'timesheets': t('dashboard.widgetTrackTime'),
      'contacts': t('dashboard.widgetManageContacts'),
      'calendar': t('dashboard.widgetViewSchedules'),
      'leave': t('dashboard.widgetManageLeave'),
      'admin': t('dashboard.widgetSystemSettings'),
      'aiChat': t('dashboard.widgetOpenAIAssistant'),
      'createMessage': t('dashboard.widgetSendMessage'),
      'notifications': t('dashboard.widgetViewNotifications'),
      'createTimesheet': t('dashboard.widgetAddTimesheet'),
      'dashboard-aiChat': t('dashboard.widgetOpenAIAssistant'),
      'dashboard-createMessage': t('dashboard.widgetSendMessage'),
      'dashboard-notifications': t('dashboard.widgetViewNotifications'),
      'dashboard-createTimesheet': t('dashboard.widgetAddTimesheet'),
      'dashboard-createLeave': t('dashboard.widgetCreateLeaveRequest')
    };
    return descMap[widgetId] || '';
  };

  useEffect(() => {
    fetchSettings();
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/subscriptions/current`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success && data.data && data.data.subscription) {
        setSubscriptionPlan(data.data.subscription.plan || 'free');
      }
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
    }
  };

  const getDefaultWidgets = () => {
    // Import defaults from Dashboard - these should match backend defaults
    return [
      // Quick Actions (Navigation Cards)
      { id: 'admin', type: 'quickAction', label: 'Admin', icon: 'TrendingUp', path: '/admin', description: 'System settings', color: 'bg-gray-500', order: 0, enabled: true },
      // Right Menu Actions
      { id: 'aiChat', type: 'rightMenuAction', label: 'AI Chat', icon: 'MessageCircle', action: 'openAIChat', description: 'Open AI assistant', order: 0, enabled: true },
      { id: 'createMessage', type: 'rightMenuAction', label: 'Create Message', icon: 'Mail', action: 'openMessaging', description: 'Send a message', order: 1, enabled: true },
      { id: 'notifications', type: 'rightMenuAction', label: 'Notifications', icon: 'Bell', action: 'openNotifications', description: 'View notifications', order: 2, enabled: true },
      { id: 'createTimesheet', type: 'rightMenuAction', label: 'Create Timesheet', icon: 'Clock', action: 'createTimesheet', description: 'Add new timesheet', order: 3, enabled: true },
      { id: 'createLeave', type: 'rightMenuAction', label: 'Create Leave', icon: 'Calendar', action: 'createLeave', description: 'Create leave request', order: 4, enabled: true }
    ];
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/dashboard-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        const savedWidgets = data.data.widgets || [];
        const defaults = getDefaultWidgets();
        
        // Merge: include all defaults, but preserve saved order/enabled state
        const mergedWidgets = defaults.map(defaultWidget => {
          const existing = savedWidgets.find((w: DashboardWidget) => w.id === defaultWidget.id);
          return existing || defaultWidget;
        });
        
        setWidgets(mergedWidgets);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError(t('admin.failedToFetchExperienceSettings'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canEdit) {
      setError(t('admin.youDoNotHavePermissionToEdit'));
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/dashboard-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          widgets: widgets.map((w, idx) => ({ ...w, order: idx })),
          isAccountWide: false
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(t('admin.experienceSettingsSavedSuccessfully'));
        setShowRefreshModal(true);
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError(t('admin.failedToSaveExperienceSettings'));
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(t('admin.resetToDefaultConfirm'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/dashboard-settings`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          isAccountWide: false
        })
      });

      const data = await response.json();
      if (data.success) {
        await fetchSettings();
        setSuccessMessage(t('admin.experienceSettingsResetSuccessfully'));
        setShowRefreshModal(true);
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError(t('admin.failedToResetExperienceSettings'));
      console.error(err);
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newWidgets = [...widgets];
    const draggedWidget = newWidgets[draggedIndex];
    newWidgets.splice(draggedIndex, 1);
    newWidgets.splice(index, 0, draggedWidget);
    
    setWidgets(newWidgets);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const toggleWidget = (index: number) => {
    if (!canEdit) return;
    
    const newWidgets = [...widgets];
    newWidgets[index].enabled = !newWidgets[index].enabled;
    setWidgets(newWidgets);
  };

  const renderIcon = (iconName: string) => {
    const IconComponent = iconMap[iconName];
    return IconComponent ? <IconComponent className="w-5 h-5" /> : null;
  };

  const quickActions = widgets.filter(w => w.type === 'quickAction');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">{t('admin.loadingExperienceSettings')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">{t('admin.mainExperienceCards')}</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('admin.mainExperienceCardsDescription')}
          </p>
          {!canEdit && (
            <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
              ℹ️ {t('admin.viewOnlyAccess')}
            </p>
          )}
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <button
              onClick={handleReset}
              disabled={saving}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              {t('admin.reset')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? t('admin.saving') : t('admin.saveChanges')}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 dark:bg-red-900 dark:border-red-700 dark:text-red-300 rounded-lg">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 text-green-700 dark:bg-green-900 dark:border-green-700 dark:text-green-300 rounded-lg">
          {successMessage}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            {t('admin.mainExperienceCards')}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {t('admin.dragToReorderToggleToEnable')}
          </p>
          
          <div className="space-y-2">
            {quickActions.map((widget, index) => {
              const actualIndex = widgets.findIndex(w => w.id === widget.id);
              return (
                <div
                  key={widget.id}
                  draggable={canEdit}
                  onDragStart={() => handleDragStart(actualIndex)}
                  onDragOver={(e) => handleDragOver(e, actualIndex)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-3 p-4 border rounded-lg transition-all ${
                    widget.enabled
                      ? 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                      : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-600 opacity-60'
                  } ${canEdit ? 'cursor-move hover:shadow-md' : ''}`}
                >
                  {canEdit && <GripVertical className="w-5 h-5 text-gray-400" />}
                  
                  <div className={`${widget.color || 'bg-gray-500'} p-2 rounded-lg text-white`}>
                    {renderIcon(widget.icon)}
                  </div>
                  
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-gray-100">{getWidgetLabel(widget.id)}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{getWidgetDescription(widget.id)}</div>
                  </div>

                  {canEdit && (
                    <button
                      onClick={() => toggleWidget(actualIndex)}
                      className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                        widget.enabled
                          ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 hover:bg-success-200 dark:hover:bg-success-800'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500'
                      }`}
                    >
                      {widget.enabled ? t('admin.enabled') : t('admin.disabled')}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

      {showRefreshModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              <div className="mb-4 flex justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-600"></div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {t('admin.applyingChanges')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {t('admin.refreshingExperience')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

